import { useLang } from "@/context/LanguageContext";

// ---------------------------------------------------------------------------
// Die offiziellen Store-Abzeichen — „Laden im App Store" und „Jetzt bei Google
// Play". Vorher standen dort zwei selbstgebaute Pillen mit Lucide-Symbolen
// („iOS" / „Android"); die erkennt niemand wieder. Die Abzeichen sind Marken:
// Apple und Google geben die Grafik vor, und sie darf nicht verändert werden —
// keine eigenen Farben, keine Deckkraft, kein Nachzeichnen. Deshalb liegen die
// Originaldateien unter public/badges (Herkunft und Regeln: public/badges/README.md).
//
// SPRACHEN: beide Anbieter liefern die Abzeichen übersetzt aus. Apple hat kein
// arabisches — dort fällt der Store selbst auf Englisch zurück, also tun wir es
// auch (APPLE_SPRACHEN kennt kein „ar").
//
// GRÖSSE: beide Bilder sind auf ihren sichtbaren Rand beschnitten, darum macht
// dieselbe Höhe (h-10 = 40px) sie auch optisch gleich groß. Breite frei laufen
// lassen — die Grafik verzerren wäre wieder eine Veränderung. Damit ist das
// Apple-Abzeichen nie kleiner als das andere (Apples Vorgabe), und der Abstand
// von 10px ist genau der Schutzraum, den Google verlangt (¼ der Höhe).
//
// SCHWARZ auf dem dunklen Fußzeilen-Panel: beide Abzeichen tragen eine helle
// Haarlinie und stehen dadurch auch auf #211F1D als Paar. Auf hellem Grund
// funktioniert dieselbe Fassung ebenfalls (die Haarlinie verschwindet dann).
// ---------------------------------------------------------------------------

// Solange die App in keinem Store liegt, sind das keine Links. Ein Abzeichen,
// das nirgendwohin führt, wäre der gleiche Fehler wie die alten href="#":
// Der Nutzer hält den Fehlschlag für seinen eigenen. Deshalb <span> statt <a>,
// nichts ist fokussierbar, und die Zeile darunter sagt, woran es liegt.
// Stand 03.08.2026: iOS liegt in TestFlight, Play Store wartet auf die
// Firmengründung (memory/RELEASE_BLOCKER_2026-07.md §4).
// ZUM SCHARFSCHALTEN: hier die Store-Adressen eintragen — sonst ändert sich nichts.
export const APP_STORE_URL = null; // z. B. "https://apps.apple.com/app/id6793437594"
export const PLAY_STORE_URL = null; // z. B. "https://play.google.com/store/apps/details?id=com.avliana.app"

// Eigenmaße der Originaldateien. Sie stehen im width/height-Attribut, damit der
// Browser den Platz kennt, bevor das Bild da ist — sonst springt die Fußzeile
// beim Nachladen.
const APPLE = { en: [120, 40], de: [120, 40], el: [120, 40], ru: [120, 40], he: [122, 42], zh: [109, 40] };
const PLAY = { en: [564, 168], de: [646, 192], el: [646, 192], ru: [646, 192], ar: [564, 168], he: [564, 168], zh: [646, 192] };

function Abzeichen({ href, src, alt, masse }) {
  const [breite, hoehe] = masse;
  const bild = (
    <img
      src={src}
      alt={alt}
      width={breite}
      height={hoehe}
      loading="lazy"
      decoding="async"
      className="block h-10 w-auto"
    />
  );
  if (!href) {
    // aria-disabled statt disabled: ein <span> kennt kein disabled, aber
    // Screenreader sollen den Zustand hören — den Grund liefert aria-describedby.
    return (
      <span aria-disabled="true" aria-describedby="app-store-hinweis" className="inline-block cursor-default">
        {bild}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // Rückmeldung über die Bewegung, nicht über die Grafik — Deckkraft oder
      // Farbe zu ändern wäre laut Markenvorgaben nicht erlaubt.
      className="inline-block transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
    >
      {bild}
    </a>
  );
}

export default function AppStoreBadges() {
  const { lang, t } = useLang();
  const apple = APPLE[lang] ? lang : "en";
  const play = PLAY[lang] ? lang : "en";
  const offen = APP_STORE_URL || PLAY_STORE_URL;

  return (
    <div>
      {/* Umbrechen statt fester Spaltenzahl: in der schmalen Fußzeilenspalte
          (lg, ~205px) passt nur eins nebeneinander, auf dem Handy spannt die
          Spalte über die volle Breite. Ein Breakpoint müsste raten. */}
      <div className="flex flex-wrap items-center gap-2.5" data-testid="app-badges">
        <Abzeichen
          href={APP_STORE_URL}
          src={`/badges/app-store-${apple}.svg`}
          alt={t("footer.app_ios_alt")}
          masse={APPLE[apple]}
        />
        <Abzeichen
          href={PLAY_STORE_URL}
          src={`/badges/google-play-${play}.png`}
          alt={t("footer.app_android_alt")}
          masse={PLAY[play]}
        />
      </div>
      {!offen && (
        <p id="app-store-hinweis" className="mt-2.5 text-xs text-white/70">
          {t("footer.app_soon")}
        </p>
      )}
    </div>
  );
}
